import { Link } from 'react-router-dom';
import type { NewsArticle } from '../../../../mocks/news';
import { categoryBadgeClass } from '../../../../services/news';
import { useTranslation } from 'react-i18next';

interface RelatedArticlesProps {
  currentId: string;
  articles: NewsArticle[];
}

export default function RelatedArticles({ currentId, articles }: RelatedArticlesProps) {
  const { t } = useTranslation();
  const related = articles.filter((a) => a.id !== currentId).slice(0, 3);
  if (related.length === 0) return null;

  return (
    <section className="bg-light-section border-t border-primary/10 py-20">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <span className="w-1 h-5 rounded-full bg-primary" />
            <h2 className="text-xl font-heading font-bold text-gray-900">{t('newsDetail.related.title')}</h2>
          </div>
          <Link to="/news" className="inline-flex items-center gap-2 text-gray-400 hover:text-primary text-sm transition-colors duration-200 cursor-pointer">
            {t('news.more')}
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-sm" /></div>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {related.map((article) => (
            <Link
              key={article.id}
              to={`/news/${article.id}`}
              className="group bg-white border border-primary/10 rounded-2xl overflow-hidden hover:border-primary/25 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
            >
              <div className="relative h-44 overflow-hidden">
                <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
                <div className="absolute top-3 left-3">
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${categoryBadgeClass(article.category)}`}>
                    {article.categoryLabel}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-gray-400 text-xs mb-2">
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}{t('newsPage.readSuffix')}</span>
                </div>
                <h3 className="text-sm font-bold text-gray-800 leading-snug mb-2 group-hover:text-primary transition-colors duration-200 line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-gray-400 text-xs leading-relaxed line-clamp-2">{article.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
